import React from 'react';
import { Typography, Alert, Table, Tag, Tabs, Card, Statistic, Row, Col } from 'antd';

const { Title, Text } = Typography;

interface MutationData {
  position: number;
  wildtype: string;
  mutation: string;
  ddG_pred: number;
  category: string;
}

interface ThermoMPNNResultProps {
  taskResult: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const ThermoMPNNResult: React.FC<ThermoMPNNResultProps> = ({ 
  taskResult, 
  data, 
  title = "ThermoMPNN 突变预测结果", 
  showTitle = true 
}) => {

  // 提取结果数据
  const getResultData = () => {
    // 格式1: Profile页面格式
    if (taskResult?.result?.results?.[0]?.result) {
      return taskResult.result.results[0].result;
    }
    // 格式2: 前端页面格式
    if (taskResult?.mutations) {
      return taskResult;
    }
    // 格式3: data参数
    if (data) {
      return data;
    }
    
    return null;
  };

  const resultData = getResultData();

  if (!taskResult) {
    return <Alert type="warning" message="无任务数据" />;
  }

  if (!resultData || !resultData.mutations) {
    return (
      <Alert
        type="info"
        message="无ThermoMPNN突变预测结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  const { summary, mutations, position_analysis } = resultData;

  const mutationColumns = [
    { title: '位置', dataIndex: 'position', key: 'position', width: 80 },
    { title: '野生型', dataIndex: 'wildtype', key: 'wildtype', width: 80 },
    { title: '突变型', dataIndex: 'mutation', key: 'mutation', width: 80 },
    { 
      title: 'ΔΔG (kcal/mol)', 
      dataIndex: 'ddG_pred', 
      key: 'ddG_pred', 
      width: 120,
      render: (val: number) => (
        <span style={{ color: val < -1 ? 'green' : val > 1 ? 'red' : 'orange' }}>
          {val.toFixed(2)}
        </span>
      ),
      sorter: (a: MutationData, b: MutationData) => a.ddG_pred - b.ddG_pred
    },
    { 
      title: '分类', 
      dataIndex: 'category', 
      key: 'category', 
      width: 100,
      render: (cat: string) => (
        <Tag color={cat === 'Stabilizing' ? 'green' : cat === 'Destabilizing' ? 'red' : 'orange'}>
          {cat === 'Stabilizing' ? '稳定化' : cat === 'Destabilizing' ? '去稳定化' : '中性'}
        </Tag>
      )
    }
  ];

  const tabItems = [
    {
      key: 'summary',
      label: '结果概览',
      children: (
        <div>
          {summary && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Statistic 
                  title="总突变数" 
                  value={summary.total_mutations || 0} 
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="稳定化突变" 
                  value={summary.stabilizing_mutations || 0} 
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="去稳定化突变" 
                  value={summary.destabilizing_mutations || 0} 
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="中性突变" 
                  value={summary.neutral_mutations || 0} 
                  valueStyle={{ color: '#d46b08' }}
                />
              </Col>
            </Row>
          )}
          
          <Table
            dataSource={mutations}
            columns={mutationColumns}
            rowKey={(record: MutationData) => `${record.position}_${record.mutation}`}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        </div>
      )
    }
  ];

  if (position_analysis) {
    tabItems.push({
      key: 'position_analysis',
      label: '位置分析',
      children: (
        <div>
          <Text>位置分析数据可在此处展示</Text>
          <pre style={{ 
            background: '#f5f5f5', 
            padding: 16, 
            borderRadius: 4,
            marginTop: 16
          }}>
            {JSON.stringify(position_analysis, null, 2)}
          </pre>
        </div>
      )
    });
  }

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>{title}</Text>
        </div>
      )}
      
      <Tabs items={tabItems} />
    </div>
  );
};

export default ThermoMPNNResult;
